import { 
  ScrollText, 
  Building2, 
  Calendar, 
  Euro, 
  FileCheck,
  AlertTriangle,
  ListChecks,
  CheckCircle2,
  ExternalLink
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { 
  EditableField, 
  EditablePROList 
} from "@/components/CountryEditor";

export default function PackagingSection({ countryCode, packaging = {}, onRefresh }) {
  return (
    <div className="space-y-4">
      {/* Overview Cards - Compact Editable */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="card p-3">
          <div className="flex items-center gap-1.5 text-zinc-500 mb-1">
            <ScrollText className="w-3.5 h-3.5" />
            <span className="text-xs">Regulation</span>
          </div>
          <EditableField
            countryCode={countryCode}
            fieldPath="packaging.regulation_name"
            value={packaging.regulation_name}
            label="Regulation Name"
            onUpdate={onRefresh}
          />
          {packaging.effective_date && (
            <p className="text-xs text-zinc-500 mt-0.5">Effective: {packaging.effective_date}</p>
          )}
        </div>
        <div className="card p-3">
          <div className="flex items-center gap-1.5 text-zinc-500 mb-1">
            <Building2 className="w-3.5 h-3.5" />
            <span className="text-xs">Registration System</span>
          </div>
          <EditableField
            countryCode={countryCode}
            fieldPath="packaging.registration_system"
            value={packaging.registration_system}
            label="Registration System"
            onUpdate={onRefresh}
          />
          {packaging.registration_url && (
            <a 
              href={packaging.registration_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-violet-600 hover:underline flex items-center gap-1 mt-0.5"
            >
              Register <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
        <div className="card p-3">
          <div className="flex items-center gap-1.5 text-zinc-500 mb-1">
            <Calendar className="w-3.5 h-3.5" />
            <span className="text-xs">Reporting</span>
          </div>
          <EditableField
            countryCode={countryCode}
            fieldPath="packaging.reporting_frequency"
            value={packaging.reporting_frequency}
            label="Reporting Frequency"
            onUpdate={onRefresh}
          />
          <div className="mt-1">
            <span className="text-xs text-zinc-500">Deadline: </span>
            <EditableField
              countryCode={countryCode}
              fieldPath="packaging.reporting_deadline"
              value={packaging.reporting_deadline}
              label="Reporting Deadline"
              onUpdate={onRefresh}
            />
          </div>
        </div>
      </div>

      {/* PROs Section - Editable */}
      <div className="card p-4">
        <h3 className="font-semibold text-zinc-800 mb-3 flex items-center gap-2 text-sm">
          <FileCheck className="w-4 h-4 text-violet-600" />
          {countryCode === "UK" ? "Compliance Schemes" : "Producer Responsibility Organizations (PROs)"}
        </h3>
        <EditablePROList
          countryCode={countryCode}
          category="packaging"
          pros={packaging.major_pros || []}
          onUpdate={onRefresh}
        />
      </div>

      {/* Fee Structure & Penalties */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {packaging.fee_structure && (
          <div className="card p-4">
            <h3 className="font-semibold text-zinc-800 mb-3 flex items-center gap-2 text-sm">
              <Euro className="w-4 h-4 text-green-600" />
              Fee Structure
            </h3>
            <p className="text-zinc-700 text-sm mb-2">{packaging.fee_structure.type}</p>
            {packaging.fee_structure.example_rates && (
              <div className="space-y-1">
                {Object.entries(packaging.fee_structure.example_rates).map(([material, rate]) => (
                  <div key={material} className="flex items-center justify-between text-xs">
                    <span className="text-zinc-600 capitalize">{material.replace(/_/g, ' ')}</span>
                    <Badge variant="outline" className="text-xs">{rate}</Badge>
                  </div>
                ))}
              </div>
            )}
            {packaging.fee_structure.eco_modulation && (
              <div className="mt-2 p-1.5 bg-green-50 rounded text-xs text-green-700">
                Eco-modulation applies
              </div>
            )}
          </div>
        )}

        {packaging.penalties && (
          <div className="card p-4">
            <h3 className="font-semibold text-zinc-800 mb-3 flex items-center gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              Penalties
            </h3>
            <div className="space-y-2">
              {Object.entries(packaging.penalties).map(([type, penalty]) => (
                <div key={type} className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0"></div>
                  <div>
                    <p className="text-xs font-medium text-zinc-700 capitalize">{type.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-zinc-500">{penalty}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Material Categories */}
      {packaging.material_categories && packaging.material_categories.length > 0 && (
        <div className="card p-4">
          <h3 className="font-semibold text-zinc-800 mb-2 text-sm">Material Categories</h3>
          <div className="flex flex-wrap gap-1.5">
            {packaging.material_categories.map((material, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">{material}</Badge>
            ))}
          </div>
        </div>
      )}

      {/* Special Requirements */}
      {packaging.special_requirements && packaging.special_requirements.length > 0 && (
        <div className="card p-4">
          <h3 className="font-semibold text-zinc-800 mb-2 flex items-center gap-2 text-sm">
            <ListChecks className="w-4 h-4 text-violet-600" />
            Special Requirements
          </h3>
          <ul className="space-y-1.5">
            {packaging.special_requirements.map((req, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="w-3.5 h-3.5 text-violet-500 mt-0.5 flex-shrink-0" />
                <span className="text-zinc-700">{req}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
